import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './CompanyFonts.module.scss';
import VideoService from '../../../../../../../service/VideoService';
import Spinner from '../../../../../../../../common/components/Spinner';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import EditFontModal from '../../../../../FontsManagement/components/EditFontModal/EditFontModal';

const FONT_ITEM_SIZE = 16;

const CompanyFonts = ({ companyId }) => {
  const [companyFontsList, setCompanyFontsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fontToEdit, setFontToEdit] = useState(null);
  const [editFontModal, setEditFontModal] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);

  const scrollableContentRef = useRef(null);

  const isListScrollable = useMemo(() => {
    const { scrollHeight, offsetHeight } = scrollableContentRef?.current || {};
    return scrollHeight - offsetHeight > 0;
  }, [scrollableContentRef.current?.scrollHeight]);

  const { t } = useTranslation();

  const getCompanyFontsList = useCallback(async () => {
    try {
      setIsLoading(true);
      const fontsList = await VideoService.getFontsList(companyId);
      setCompanyFontsList(fontsList);
    } catch (err) {
      console.log(err);
    }

    setIsLoading(false);
  }, [companyId]);

  const handleOpenFontModal = async id => {
    setEditFontModal(true);
    const fontDetails = await VideoService.getFontById(id);
    setAllCompanies(fontDetails?.companies);
    setFontToEdit(fontDetails);
  };

  const handleCloseFontModal = () => {
    setEditFontModal(false);
    setTimeout(() => {
      setFontToEdit(null);
    }, 250);
  };

  const handleUpdateFont = async (id, isDefault = null, companies = null, newName = null) => {
    const payload = {};
    if (isDefault !== null) {
      payload.default = isDefault;
    }
    if (companies !== null) {
      payload.companies = companies;
    }
    if (newName) {
      payload.name = newName;
    }
    await VideoService.updateFont(id, payload);

    getCompanyFontsList();
  };

  const checkIfFontLoadedOrLoad = async fonts => {
    await Promise.all(
      fonts.map(async fontItem => {
        try {
          //If we specify a font that is not in the FontFaceSet and is not a system font, check() returns true, because in this situation we will not rely on any fonts from the set:
          if (document.fonts.check(`${FONT_ITEM_SIZE}px '${fontItem?.name}'`)) {
            var labelFont = new FontFace(fontItem?.name, `url(${fontItem?.url})`);
            const loadedFace = await labelFont.load();
            document.fonts.add(loadedFace);
          }
        } catch (e) {
          console.log('Font loaded failed with error:', e);
        }
      })
    );
  };

  useEffect(() => {
    getCompanyFontsList();
  }, [companyId]);

  useEffect(() => {
    checkIfFontLoadedOrLoad(companyFontsList);
  }, [companyFontsList]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerContainer}>
        <h2 className={styles.header}>{t('company.settings.fonts.company_fonts')} {`(${companyFontsList?.length})`}</h2>
      </div>
      <div className={styles.fontsTable}>
        <div className={styles.fontsTableHeader}>
          <div className={styles.itemHeader}>
            <span>{t('company.settings.users.name')}</span>
          </div>
        </div>
        {isLoading ? (
          <div className={styles.spinnerWrapper}>
            <Spinner size="3px" width="40px" height="40px" />
          </div>
        ) : (
          <ul ref={scrollableContentRef} className={cx(styles.fontList, { [styles.scrollableFontList]: isListScrollable })}>
            {companyFontsList.map(({ id, name, default: isDefault }) => (
              <li key={id} className={styles.fontItem} onClick={() => handleOpenFontModal(id)}>
                <span className={styles.fontName} style={{ fontFamily: `'${name}'` }}>
                  {name}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <EditFontModal
        allCompanies={allCompanies}
        open={editFontModal}
        onClose={handleCloseFontModal}
        fontData={fontToEdit}
        onSave={handleUpdateFont}
      />
    </div>
  );
};

export default CompanyFonts;
